import type { ReactNode } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EmptyState } from './EmptyState';

export type Column<T> = {
  header: string;
  render: (row: T) => ReactNode;
  /** on a phone: shown big at the top of the card */
  primary?: boolean;
  /** on a phone: shown at the right of the top line (buttons) */
  action?: boolean;
  align?: 'right';
  /** keep dates, amounts and chips on one line */
  nowrap?: boolean;
  /** long text: at most this many lines, then "…" (the full text shows on hover); the column stays narrow */
  clamp?: number;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  empty?: string;
  /** makes every row clickable */
  onRowClick?: (row: T) => void;
  selected?: (row: T) => boolean;
};

function Clamped({ lines, children }: { lines: number; children: ReactNode }) {
  return (
    <Box
      title={typeof children === 'string' ? children : undefined}
      sx={{ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
    >
      {children}
    </Box>
  );
}

/** A table on a Mac; on a phone every row becomes a small card, so nothing has to scroll sideways */
export function DataTable<T>({ rows, columns, rowKey, empty = 'Nothing yet.', onRowClick, selected }: Props<T>) {
  const phone = useMediaQuery(useTheme().breakpoints.down('sm'));
  if (!rows.length) return <EmptyState>{empty}</EmptyState>;

  if (phone) {
    const top = columns.filter((c) => c.primary);
    const action = columns.filter((c) => c.action);
    const rest = columns.filter((c) => !c.primary && !c.action);
    return (
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {rows.map((row) => (
          <Stack
            key={rowKey(row)}
            spacing={0.75}
            onClick={onRowClick && (() => onRowClick(row))}
            sx={{
              py: 1.25,
              cursor: onRowClick ? 'pointer' : undefined,
              bgcolor: selected?.(row) ? 'action.selected' : undefined,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                {top.map((c) => (
                  <Box key={c.header} sx={{ fontWeight: 600 }}>
                    {c.render(row)}
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
                {action.map((c) => (
                  <Box key={c.header || 'action'}>{c.render(row)}</Box>
                ))}
              </Stack>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {rest.map((c) => (
                <Box key={c.header} sx={{ fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {c.header}:{' '}
                  </Typography>
                  {c.render(row)}
                </Box>
              ))}
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.header} align={c.align}>
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              hover
              selected={selected?.(row)}
              onClick={onRowClick && (() => onRowClick(row))}
              sx={{ cursor: onRowClick ? 'pointer' : undefined, '&:last-child td': { borderBottom: 0 } }}
            >
              {columns.map((c) => (
                <TableCell
                  key={c.header}
                  align={c.align}
                  sx={c.nowrap ? { whiteSpace: 'nowrap' } : c.clamp ? { minWidth: 90, maxWidth: 170 } : undefined}
                >
                  {c.clamp ? <Clamped lines={c.clamp}>{c.render(row)}</Clamped> : c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
